import { Button, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import { ScheduleUpdateScope } from '../../types/MachineDetailsRobotSchedule.types';
import {
  DeleteButton,
  DeleteScopeOptions,
  IconContainer,
  ScheduleDeleteModalContainer,
} from './ScheduleDeleteModal.styles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { Radio } from 'lib/components/Radio/Radio';

export const ScheduleDeleteModal = (): JSX.Element => {
  const { t } = useTranslation();

  const {
    isDeleteModalOpen,
    handleCloseDeleteModal,
    handleDelete,
    handleAfterCloseModal,
    isDeleteScheduleLoading,
    isB50,
    isSelectedEventRepeating,
    deleteScope,
    handleDeleteScopeChange,
  } = useMachineDetailsRobotSchedule();

  const isB50NonRepeatingTask = isB50 && !isSelectedEventRepeating;
  const isLoading = !!isDeleteScheduleLoading;

  const repeating = {
    title: t('scheduleDeleteModal.repeatingTitle'),
    description: t('scheduleDeleteModal.repeatingDescription'),
    deleteScope: (): JSX.Element => (
      <DeleteScopeOptions>
        <Radio.Group
          value={deleteScope}
          onChange={(event): void => handleDeleteScopeChange(event.target.value as ScheduleUpdateScope)}
          disabled={isLoading}
        >
          <Flex vertical gap={kccThemeConstants.SPACING.MD}>
            <Radio value={ScheduleUpdateScope.ThisEventOnly}>{t('scheduleUpdateConfirmModal.thisEventOnly')}</Radio>
            <Radio value={ScheduleUpdateScope.ThisAndFollowing}>
              {t('scheduleUpdateConfirmModal.thisAndFollowingEvents')}
            </Radio>
          </Flex>
        </Radio.Group>
      </DeleteScopeOptions>
    ),
  };

  const cv50 = {
    title: t('scheduleDeleteModal.cv50Title'),
    description: t('scheduleDeleteModal.cv50Description'),
    deleteScope: (): JSX.Element => <></>,
  };

  const nonRepeating = {
    title: t('scheduleDeleteModal.title'),
    description: t('scheduleDeleteModal.description'),
    deleteScope: (): JSX.Element => <></>,
  };

  const renderRelevantContent = (): { title: string; description: string; deleteScope: () => JSX.Element } => {
    if (isB50NonRepeatingTask) {
      return nonRepeating;
    }

    return isB50 ? repeating : cv50;
  };

  return (
    <ScheduleDeleteModalContainer
      title={
        <Flex align="center" justify="space-between">
          {renderRelevantContent().title}

          <Button
            type="link"
            aria-label={t('common.close')}
            onClick={handleCloseDeleteModal}
            style={{ width: kccThemeConstants.SIZE.S300, height: kccThemeConstants.SIZE.S300, padding: 0 }}
          >
            <IconContainer>
              <SvgIcon name="close" />
            </IconContainer>
          </Button>
        </Flex>
      }
      open={isDeleteModalOpen}
      onCancel={handleCloseDeleteModal}
      centered
      closable={false}
      destroyOnClose={true}
      afterClose={handleAfterCloseModal}
      footer={[
        <Button
          key="schedule-delete-cancel"
          onClick={handleCloseDeleteModal}
          style={{ minWidth: '100px', width: 'fit-content' }}
        >
          {t('common.cancel')}
        </Button>,
        <DeleteButton
          key="schedule-delete"
          type="primary"
          onClick={handleDelete}
          loading={isLoading}
          style={{ minWidth: '100px', width: 'fit-content' }}
        >
          {t('common.deleteSchedule')}
        </DeleteButton>,
      ]}
    >
      <Typography.Text>{renderRelevantContent().description}</Typography.Text>

      {renderRelevantContent().deleteScope()}
    </ScheduleDeleteModalContainer>
  );
};
